export interface AccessTokenOptions {
  forceRefresh?: boolean;
}

type AccessTokenGetter = (options?: AccessTokenOptions) => Promise<string>;

let accessTokenGetter: AccessTokenGetter | undefined;

export function registerAccessTokenGetter(getter: AccessTokenGetter): void {
  accessTokenGetter = getter;
}

export async function getAccessToken(options?: AccessTokenOptions): Promise<string> {
  if (!accessTokenGetter) {
    throw new Error('getAccessToken called before registerAccessTokenGetter — is AuthProvider mounted?');
  }
  return accessTokenGetter(options);
}

const LOGIN_ATTEMPT_KEY = 'auth:repo-auth-login-attempts';
const MAX_LOGIN_ATTEMPTS = 2;

const LOGIN_REDIRECT_IN_FLIGHT_TIMEOUT_MS = 5_000;

let registeredLoginRedirect: ((returnTo?: string) => void) | null = null;
let registeredUnrecoverableSessionHandler: (() => void) | null = null;
let loginRedirectInFlight = false;

export function registerLoginRedirect(handler: (returnTo?: string) => void): void {
  registeredLoginRedirect = handler;
}

export function isLoginRedirectRegistered(): boolean {
  return registeredLoginRedirect !== null;
}

export function registerUnrecoverableSessionHandler(handler: () => void): void {
  registeredUnrecoverableSessionHandler = handler;
}

function readLoginAttempts(): number {
  return Number(window.sessionStorage.getItem(LOGIN_ATTEMPT_KEY) ?? '0');
}


// Must not clear while a redirect is in flight — an unrelated request succeeding on a
// stale-but-not-yet-expired token would otherwise erase the attempt count a genuinely
// dead session just incremented, so MAX_LOGIN_ATTEMPTS never trips.
export function clearLoginAttempts(): void {
  if (loginRedirectInFlight) return;
  window.sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
}

function runLoginRedirect(returnTo?: string): void {
  loginRedirectInFlight = true;
  setTimeout(() => {
    loginRedirectInFlight = false;
  }, LOGIN_REDIRECT_IN_FLIGHT_TIMEOUT_MS);

  registeredLoginRedirect!(returnTo);
}

export function redirectToLogin(): void {
  if (loginRedirectInFlight || !registeredLoginRedirect) return;

  const attempts = readLoginAttempts();
  if (attempts >= MAX_LOGIN_ATTEMPTS) {
    console.error(
      'redirectToLogin: giving up after reaching the maximum login attempts; the session could not be recovered automatically.'
    );
    registeredUnrecoverableSessionHandler?.();
    return;
  }
  window.sessionStorage.setItem(LOGIN_ATTEMPT_KEY, String(attempts + 1));
  runLoginRedirect();
}

export function redirectToLoginForProtectedRoute(returnTo?: string): void {
  if (loginRedirectInFlight || !registeredLoginRedirect) return;
  runLoginRedirect(returnTo);
}
